import { useCallback, useMemo, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';

import { MethodPresetCard } from '../../components/onboarding/MethodPresetCard';
import { WizardErrorCallout, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import { budgetsAPI } from '../../data/budgets';
import {
  METHOD_COLLAPSED_COPY,
  ONBOARDING_METHOD_PRESET_IDS,
} from '../../lib/onboardingCatalogs';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';

function percentagesFromPreset(preset) {
  return (preset?.groups || []).map((group) => Number(group.target_percentage));
}

function percentagesFromActiveMethod(activeMethod) {
  return [...(activeMethod?.groups || [])]
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map((group) => Number(group.target_percentage));
}

/** Resolve selected preset id from an already-created active method, else default. */
export function resolveInitialMethodId(presets, activeMethod) {
  const activeId = activeMethod?.preset_id;
  if (activeId && (presets || []).some((p) => p.id === activeId)) {
    return activeId;
  }
  return (
    (presets || []).find((p) => p.id === 'fifty_thirty_twenty')?.id ||
    presets?.[0]?.id ||
    null
  );
}

/** Percentages from active method when it matches selection, else preset defaults. */
export function resolveInitialPercentages(presets, activeMethod, selectedId) {
  if (
    activeMethod?.preset_id === selectedId &&
    Array.isArray(activeMethod?.groups) &&
    activeMethod.groups.length > 0
  ) {
    return percentagesFromActiveMethod(activeMethod);
  }
  return percentagesFromPreset((presets || []).find((p) => p.id === selectedId));
}

async function methodLoader() {
  const [presetsRes, activeRes] = await Promise.all([
    budgetsAPI.getPresets(),
    budgetsAPI.getActiveMethod(),
  ]);
  const presets = presetsRes.success ? presetsRes.data : [];
  const filtered = ONBOARDING_METHOD_PRESET_IDS.map((id) =>
    presets.find((p) => p.id === id),
  ).filter(Boolean);
  return {
    presets: filtered.length ? filtered : presets.slice(0, 3),
    activeMethod: activeRes.success ? activeRes.data : null,
  };
}

export function OnboardingMethod() {
  const { presets, activeMethod } = useLoaderData();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const [selected, setSelected] = useState(() =>
    resolveInitialMethodId(presets, activeMethod),
  );
  const [percentages, setPercentages] = useState(() =>
    resolveInitialPercentages(presets, activeMethod, resolveInitialMethodId(presets, activeMethod)),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const total = useMemo(
    () => percentages.reduce((sum, value) => sum + value, 0),
    [percentages],
  );
  const isValid = total === 100 && percentages.length > 0;

  const onSelect = useCallback(
    (presetId) => {
      setSelected(presetId);
      const preset = presets.find((p) => p.id === presetId);
      // Prefer saved % when re-selecting the active method; else preset defaults
      if (activeMethod?.preset_id === presetId && activeMethod.groups?.length) {
        setPercentages(percentagesFromActiveMethod(activeMethod));
      } else {
        setPercentages(percentagesFromPreset(preset));
      }
      setError(null);
    },
    [activeMethod, presets],
  );

  const onPercentageChange = useCallback((index, [value]) => {
    setPercentages((current) =>
      current.map((existing, idx) => (idx === index ? value : existing)),
    );
  }, []);

  const onContinue = useCallback(async () => {
    if (!selected || !isValid) return;
    const preset = presets.find((p) => p.id === selected);
    if (!preset) return;

    setLoading(true);
    setError(null);

    const payload = {
      preset_id: selected,
      customisations: preset.groups.map((group, index) => ({
        name: group.name,
        color: group.color,
        icon: group.icon,
        target_percentage: Number(percentages[index]).toFixed(2),
      })),
    };

    const response = await budgetsAPI.createMethodFromPreset(payload);
    if (!response.success) {
      setError(
        typeof response.error === 'string'
          ? response.error
          : response.error?.detail || pt.auth.errors.generic,
      );
      setLoading(false);
      return;
    }

    await authAPI.saveOnboardingStep('income_sources');
    await refreshMe();
    setLoading(false);
    navigate('/onboarding/income-sources');
  }, [isValid, navigate, percentages, presets, refreshMe, selected]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    loading,
    disabled: !selected || !isValid,
  });

  return (
    <div className="flex flex-col gap-space-6">
      <WizardStepHeader
        eyebrow={pt.onboarding.method.eyebrow}
        title={pt.onboarding.method.title}
        subtitle={pt.onboarding.method.subtitle}
      />

      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
        {presets.map((preset) => {
          const recommended = preset.id === 'fifty_thirty_twenty';
          const isSelected = selected === preset.id;
          return (
            <MethodPresetCard
              key={preset.id}
              preset={preset}
              selected={isSelected}
              onSelect={onSelect}
              recommended={recommended}
              collapsedCopy={METHOD_COLLAPSED_COPY[preset.id]}
              className={recommended ? 'md:col-span-2 lg:col-span-1' : undefined}
              percentages={isSelected ? percentages : undefined}
              onPercentageChange={isSelected ? onPercentageChange : undefined}
              total={isSelected ? total : undefined}
              isValid={isSelected ? isValid : undefined}
            />
          );
        })}
      </div>

      {error ? <WizardErrorCallout>{error}</WizardErrorCallout> : null}
    </div>
  );
}

OnboardingMethod.loader = methodLoader;
export { methodLoader };
